import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { getCurrentIdentity, type CfAccessIdentity } from "./cfAccess";
import {
  getSupabaseSession,
  isSupabaseConfigured,
  onSupabaseAuthChange,
} from "./supabaseAuth";

/**
 * Auth state. Supabase Auth + Postgres RLS is the SOLE gate (Cloudflare Access
 * was removed 2026-06 — it collided with the magic-link callback on mobile).
 * Identity is derived from the Supabase session; a missing session means
 * "log in", NOT "denied" — anyone can reach the login screen, RLS decides what
 * they can see once authenticated.
 *
 *   loading        — checking the Supabase session
 *   needs_login    — no session yet -> magic-link form
 *   authenticated  — session present; `user` is derived from it. `session` is
 *                    null only in local dev without Supabase (dev-shim identity).
 */
export type AuthState =
  | { status: "loading" }
  | { status: "needs_login" }
  | {
      status: "authenticated";
      user: CfAccessIdentity;
      session: Session | null;
    };

const AuthContext = createContext<AuthState>({ status: "loading" });

/** Build the app's identity object from a Supabase session. */
function identityFromSession(session: Session): CfAccessIdentity {
  const email = (session.user.email ?? "").toLowerCase();
  const meta = (session.user.user_metadata ?? {}) as Record<string, unknown>;
  const name =
    typeof meta.name === "string" ? meta.name : email.split("@")[0] || undefined;
  return { email, name, sub: session.user.id, amr: ["supabase"], isDev: false };
}

interface ProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: ProviderProps) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    async function init() {
      // Local dev without Supabase configured: fall back to the dev-shim
      // identity (?dev_email / VITE_DEV_DEFAULT_EMAIL). Never used in prod
      // builds (import.meta.env.DEV is false there).
      if (!isSupabaseConfigured()) {
        const dev = await getCurrentIdentity();
        if (cancelled) return;
        setState(
          dev
            ? { status: "authenticated", user: dev, session: null }
            : { status: "needs_login" },
        );
        return;
      }

      const session = await getSupabaseSession();
      if (cancelled) return;
      setState(
        session
          ? {
              status: "authenticated",
              user: identityFromSession(session),
              session,
            }
          : { status: "needs_login" },
      );

      // Watch for magic-link return / sign-out / token refresh.
      unsubscribe = onSupabaseAuthChange((newSession) => {
        if (cancelled) return;
        setState(
          newSession
            ? {
                status: "authenticated",
                user: identityFromSession(newSession),
                session: newSession,
              }
            : { status: "needs_login" },
        );
      });
    }

    init();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/**
 * The current user identity, only when fully authenticated (Supabase session
 * present). Use inside the war room - anywhere a Supabase query is expected.
 */
export function useCurrentUser(): CfAccessIdentity | null {
  const state = useAuth();
  return state.status === "authenticated" ? state.user : null;
}
